import { type MessageKey } from '@ecom/i18n';
import { type BadgeTone } from '@ecom/ui-kit';

import type { CheckOutcome, ConnectionState, JibexAccountView } from './types';

/**
 * Lot L5a — how the screen speaks about the connection. Every map is a `Record` over the FULL union, so a state or an
 * outcome the API gains later fails to COMPILE here instead of rendering a blank badge.
 */

export const STATE_TONE: Record<ConnectionState, BadgeTone> = {
  not_configured: 'neutral',
  untested: 'info',
  connected: 'good',
  invalid: 'critical',
  misconfigured: 'warn',
  network_error: 'warn',
};

export const STATE_LABEL_KEY: Record<ConnectionState, MessageKey> = {
  not_configured: 'merchant.delivery.account.state.not_configured',
  untested: 'merchant.delivery.account.state.untested',
  connected: 'merchant.delivery.account.state.connected',
  invalid: 'merchant.delivery.account.state.invalid',
  misconfigured: 'merchant.delivery.account.state.misconfigured',
  network_error: 'merchant.delivery.account.state.network_error',
};

export const STATE_TEXT_KEY: Record<ConnectionState, MessageKey> = {
  not_configured: 'merchant.delivery.account.stateText.not_configured',
  untested: 'merchant.delivery.account.stateText.untested',
  connected: 'merchant.delivery.account.stateText.connected',
  invalid: 'merchant.delivery.account.stateText.invalid',
  misconfigured: 'merchant.delivery.account.stateText.misconfigured',
  network_error: 'merchant.delivery.account.stateText.network_error',
};

export const OUTCOME_TONE: Record<CheckOutcome, BadgeTone> = {
  credentials_saved: 'info',
  connected: 'good',
  invalid_credentials: 'critical',
  misconfigured: 'warn',
  network_error: 'warn',
  rate_limited: 'warn',
  unavailable: 'neutral',
  disconnected: 'neutral',
};

export const OUTCOME_LABEL_KEY: Record<CheckOutcome, MessageKey> = {
  credentials_saved: 'merchant.delivery.account.outcome.credentials_saved',
  connected: 'merchant.delivery.account.outcome.connected',
  invalid_credentials: 'merchant.delivery.account.outcome.invalid_credentials',
  misconfigured: 'merchant.delivery.account.outcome.misconfigured',
  network_error: 'merchant.delivery.account.outcome.network_error',
  rate_limited: 'merchant.delivery.account.outcome.rate_limited',
  unavailable: 'merchant.delivery.account.outcome.unavailable',
  disconnected: 'merchant.delivery.account.outcome.disconnected',
};

export interface ProblemMessage {
  readonly key: MessageKey;
  /** Present only for the generic fallback, which names the code. */
  readonly params?: { readonly code: string };
}

const PROBLEM_KEY_BY_CODE: Readonly<Record<string, MessageKey>> = {
  INVALID_CLIENT: 'merchant.delivery.account.problem.invalidClient',
  CLIENT_INACTIVE: 'merchant.delivery.account.problem.clientInactive',
  FORBIDDEN: 'merchant.delivery.account.problem.forbidden',
  TOKEN_REJECTED: 'merchant.delivery.account.problem.forbidden',
  ECOMMERCE_STORE_NOT_FOUND: 'merchant.delivery.account.problem.storeNotFound',
  RATE_LIMITED: 'merchant.delivery.account.problem.rateLimited',
  TIMEOUT: 'merchant.delivery.account.problem.timeout',
  CONNECT_TIMEOUT: 'merchant.delivery.account.problem.timeout',
  NETWORK_ERROR: 'merchant.delivery.account.problem.unreachable',
  CARRIER_UNREACHABLE: 'merchant.delivery.account.problem.unreachable',
  ENOTFOUND: 'merchant.delivery.account.problem.unreachable',
  TLS_ERROR: 'merchant.delivery.account.problem.unreachable',
  RETRIES_EXHAUSTED: 'merchant.delivery.account.problem.unreachable',
  CIRCUIT_OPEN: 'merchant.delivery.account.problem.unreachable',
  INTERNAL_ERROR: 'merchant.delivery.account.problem.unreachable',
  UNEXPECTED_STATUS: 'merchant.delivery.account.problem.unreachable',
  INVALID_RESPONSE: 'merchant.delivery.account.problem.unreachable',
  REDIRECT_REFUSED: 'merchant.delivery.account.problem.unreachable',
  RESPONSE_TOO_LARGE: 'merchant.delivery.account.problem.unreachable',
  CARRIER_NOT_CONFIGURED: 'merchant.delivery.account.problem.notActivated',
  SSRF_BLOCKED: 'merchant.delivery.account.problem.notActivated',
  SOUK_SSRF_BLOCKED_ADDRESS: 'merchant.delivery.account.problem.notActivated',
  TENANT_MISMATCH: 'merchant.delivery.account.problem.notActivated',
  CREDENTIALS_MISSING: 'merchant.delivery.account.problem.secretUnreadable',
  CREDENTIALS_UNREADABLE: 'merchant.delivery.account.problem.secretUnreadable',
};

/**
 * Turns an error CODE from the API into something a merchant can read. A code this screen does not know falls back to a
 * generic sentence that names the code (so support can still be told what it was) — never a raw server message.
 */
export function problemMessage(code: string | null): ProblemMessage | null {
  if (code === null) return null;
  const key = PROBLEM_KEY_BY_CODE[code];
  if (key !== undefined) return { key };
  return { key: 'merchant.delivery.account.problem.unknown', params: { code } };
}

/** The states in which the stored problem code is worth showing under the headline. */
export function showsProblem(state: ConnectionState): boolean {
  return state === 'invalid' || state === 'misconfigured' || state === 'network_error';
}

export interface TestAvailability {
  readonly allowed: boolean;
  /** Why not — an i18n key, shown next to the button. */
  readonly reasonKey?: MessageKey;
}

/**
 * Whether « Tester la connexion » may be pressed. It tests the SAVED credentials, so unsaved edits block it (the owner
 * would otherwise believe they tested what they just typed).
 */
export function testAvailability(
  view: JibexAccountView,
  formIsDirty: boolean,
  busy: boolean,
): TestAvailability {
  if (view.state === 'not_configured') {
    return { allowed: false, reasonKey: 'merchant.delivery.account.test.saveFirst' };
  }
  if (formIsDirty) {
    return { allowed: false, reasonKey: 'merchant.delivery.account.form.unsavedHint' };
  }
  if (!view.platformReady) {
    return { allowed: false, reasonKey: 'merchant.delivery.account.platformNotReady' };
  }
  return { allowed: !busy };
}
